import React, { useEffect, useState } from 'react';
import { Link2, Clock, Tag, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';

interface RelatedArticle {
  id: string;
  title: string;
  summary: string;
  tags: string[];
  category: string;
  similarity_score: number;
  relationship_type: string;
  direction: string;
  created_at: string;
}

interface RelatedArticlesProps {
  contentId: string;
  className?: string;
  maxItems?: number;
}

export default function RelatedArticles({ contentId, className = '', maxItems = 5 }: RelatedArticlesProps) {
  const [relatedArticles, setRelatedArticles] = useState<RelatedArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      if (!contentId || !token) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`/api/content/${contentId}/related?limit=${maxItems}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch related articles');
        }

        const data = await response.json();
        setRelatedArticles(data.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load related articles');
        console.error('Error fetching related articles:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [contentId, token, maxItems]);

  const getSimilarityColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-50';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-50';
    return 'text-blue-600 bg-blue-50';
  };

  const getSimilarityLabel = (score: number) => {
    if (score >= 0.8) return 'Very Similar';
    if (score >= 0.6) return 'Similar';
    return 'Related';
  };

  const getRelationshipIcon = (type: string) => {
    switch (type) {
      case 'similar':
        return <Link2 className="w-3 h-3" />;
      case 'references':
        return <TrendingUp className="w-3 h-3" />;
      default:
        return <Link2 className="w-3 h-3" />;
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Related Articles</h3>
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Related Articles</h3>
        </div>
        <div className="text-sm text-gray-500 text-center py-4">
          {error}
        </div>
      </div>
    );
  }

  if (relatedArticles.length === 0) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Related Articles</h3>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
          <div className="w-8 h-8 mx-auto mb-2 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <Link2 className="w-4 h-4 text-gray-400" />
          </div>
          <p>No related articles found yet.</p>
          <p className="text-xs mt-1">Save more content to see connections!</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Related Articles</h3>
        </div>
        <span className="text-xs text-gray-500">
          {relatedArticles.length} connection{relatedArticles.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-3">
        {relatedArticles.map((article, index) => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group cursor-pointer border border-gray-100 rounded-lg p-3 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-gray-900 text-sm line-clamp-1 group-hover:text-blue-700 transition-colors">
                    {article.title}
                  </h4>
                  <div className="flex items-center gap-1">
                    {getRelationshipIcon(article.relationship_type)}
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                  {article.summary}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {/* Similarity Score */}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSimilarityColor(article.similarity_score)}`}>
                      {getSimilarityLabel(article.similarity_score)}
                    </span>

                    {/* Category */}
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {article.category}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(article.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Tags */}
                {article.tags.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <Tag className="w-3 h-3 text-gray-400" />
                    {article.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                    {article.tags.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{article.tags.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Similarity Score Visual */}
              <div className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center relative">
                  <div
                    className="absolute inset-0 rounded-full border-2 border-blue-500"
                    style={{
                      clipPath: `polygon(0 0, 0 ${100 - article.similarity_score * 100}%, 100% ${100 - article.similarity_score * 100}%, 100% 0)`,
                    }}
                  ></div>
                  <span className="text-xs font-medium text-gray-700 z-10">
                    {Math.round(article.similarity_score * 100)}
                  </span>
                </div>
                <span className="text-xs text-gray-400">match</span>
              </div>
            </div>

            {/* Connection Type Indicator */}
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  Connection: {article.relationship_type}
                </span>
                <span className="text-gray-400">
                  {article.direction === 'outgoing' ? '→' : '←'} 
                  {article.direction === 'outgoing' ? ' references this' : ' referenced by this'}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* View All Link */}
      {relatedArticles.length >= maxItems && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <button className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors">
            View all related articles →
          </button>
        </div>
      )}
    </div>
  );
}